import React, { useState, useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Checkbox from '@material-ui/core/Checkbox';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import {
  EMPLOYEE_STATUS,
  EmployeeStatus,
} from '../../../employees/actions/load-org-employees-by-status';
import { setOrgEmployeesDirty } from '../../../employees/actions/set-org-employees-dirty';
import { EmployeeSelector } from '../../../widgets/employee-selector';
import { employeeDirectoryImportForIdSelector } from '../../employee-directory-import-selectors';

import * as Api from '../../../api/api';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';

import { ICON_NAMES, MaterialDesignIcon } from '../../../util/material-design-icons';
import { timeoutPromise } from '../../../util/timeout-promise';
import Message from '../../../widgets/legacy-message';
import { ReactComponent as LoadingLogo } from '../../../widgets/loading-logo.svg';
import { TableHeaderCell } from '../../../widgets/table-components/table-header-cell';

const mapStateToProps = (
  state: any,
  { employeeDirectoryImportId }: EmployeeDirectoryWizardStepProps,
) => ({
  employeeDirectoryImport: employeeDirectoryImportForIdSelector(state, employeeDirectoryImportId),
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    setOrgEmployeesDirty: (orgId: number, employeeStatus: EmployeeStatus) =>
      dispatch(setOrgEmployeesDirty(orgId, employeeStatus)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = EmployeeDirectoryWizardStepProps & ReduxProps & DispatchProps;

export const EmployeeDirectoryImportSupervisors = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    employeeDirectoryImportId,
    employeeDirectoryImport,
    onNext,
    onCancel,
    PageIndicator,
    actions,
  }: Props) => {
    const [supervisorResponse, setSupervisorResponse] =
      useState<MatchingSupervisorsResponse | null>(null);
    const [supervisorMap, setSupervisorMap] = useState<{ [supervisorName: string]: number | null }>(
      {},
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [hasInitialized, setHasInitialized] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
      if (!hasInitialized) {
        actions.setOrgEmployeesDirty(employeeDirectoryImport.orgId, EMPLOYEE_STATUS.CURRENT);
        actions.setOrgEmployeesDirty(employeeDirectoryImport.orgId, EMPLOYEE_STATUS.FORMER);

        const getSupervisors = () =>
          Api.get(`/employee-directory-import/${employeeDirectoryImportId}/supervisors`)
            .then((resp) => {
              resp.json().then((data: MatchingSupervisorsResponse | { errorMessage: string }) => {
                if ('errorMessage' in data || typeof data !== 'object') {
                  setErrorMessage('Failed to load data');
                } else if (data.supervisorEmployeeIds.length === 0) {
                  onNext();
                } else {
                  setSupervisorResponse(data);
                  data.supervisorEmployeeIds
                    .filter(
                      (row) =>
                        !row.employeeId &&
                        !supervisorMap[`${row.supervisorFirstName}${row.supervisorLastName}`],
                    )
                    .map((row) =>
                      setSupervisorMap({
                        ...supervisorMap,
                        [`${row.supervisorFirstName}${row.supervisorLastName}`]: null,
                      }),
                    );
                }
              });
            })
            .catch(() => {
              setErrorMessage('Failed to load data');
            });

        timeoutPromise(getSupervisors, () =>
          setErrorMessage('Request to get supervisor data timed out'),
        );

        setHasInitialized(true);
      }
    }, [
      hasInitialized,
      actions,
      employeeDirectoryImport.orgId,
      employeeDirectoryImportId,
      onNext,
      supervisorMap,
    ]);

    if (!employeeDirectoryImport) {
      return null;
    }

    const handleSupervisorMappingSelection =
      (supervisorMapKey: string) => (employeeId: number | null) => {
        setSupervisorMap({
          ...supervisorMap,
          [supervisorMapKey]: employeeId,
        });
      };

    const handleCreateNewEmployeeClick = (supervisorMapKey: string) => () => {
      setSupervisorMap({
        ...supervisorMap,
        [supervisorMapKey]: null,
      });
    };

    const handleOnNext = async () => {
      setIsSaving(true);

      const postSupervisors = () =>
        Api.post(`/employee-directory-import/${employeeDirectoryImportId}/supervisors`, {
          body: JSON.stringify({ supervisorMap }),
        })
          .then((resp: any) => {
            const result = resp?.json();
            setIsSaving(false);
            if (!result || result.errorMessage) {
              setErrorMessage('There was an error saving data');
            } else {
              onNext();
            }
          })
          .catch(() => {
            setErrorMessage('There was an error saving data');
          });

      timeoutPromise(postSupervisors, () =>
        setErrorMessage('Request to create and update supervisors timed out'),
      );
    };

    return (
      <>
        <UiDialog.Content>
          <PageIndicator />
          <h3>Supervisors Present</h3>
          <p className="typo-subtitle">
            These supervisors were present in the list. The ones that have a check mark were able to
            be matched automatically by name. Supervisors who could not be matched can be manually
            matched with the autocomplete, or by indicating that we should just create a new
            employee for this supervisor.
          </p>
          {errorMessage ? <Message variant="error">{errorMessage}</Message> : null}
          {!supervisorResponse && !errorMessage ? (
            <div className="u-textAlign-center">
              <LoadingLogo />
            </div>
          ) : null}
          {supervisorResponse ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Supervisor First Name</TableHeaderCell>
                  <TableHeaderCell>Supervisor Last Name</TableHeaderCell>
                  <TableHeaderCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {supervisorResponse.supervisorEmployeeIds.map((row) => (
                  <TableRow key={`${row.supervisorFirstName}${row.supervisorLastName}`}>
                    <TableCell>{row.supervisorFirstName}</TableCell>
                    <TableCell>{row.supervisorLastName}</TableCell>
                    <TableCell>
                      {row.employeeId ? (
                        <MaterialDesignIcon iconName={ICON_NAMES.CHECK_CIRCLE} />
                      ) : (
                        <>
                          <EmployeeSelector
                            orgId={employeeDirectoryImport.orgId}
                            value={
                              supervisorMap[`${row.supervisorFirstName}${row.supervisorLastName}`]
                            }
                            onChange={handleSupervisorMappingSelection(
                              `${row.supervisorFirstName}${row.supervisorLastName}`,
                            )}
                          />
                          <div className="u-margin-top-sm u-flex-center">
                            <Checkbox
                              className="u-no-padding"
                              checked={
                                !supervisorMap[
                                  `${row.supervisorFirstName}${row.supervisorLastName}`
                                ]
                              }
                              onChange={handleCreateNewEmployeeClick(
                                `${row.supervisorFirstName}${row.supervisorLastName}`,
                              )}
                            />
                            <div className="u-margin-left-sm">Create New Employee</div>
                          </div>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </UiDialog.Content>
        <UiDialog.Actions>
          <UiButton variant="dismiss" onClick={onCancel}>
            Cancel
          </UiButton>
          <UiButton
            variant="primary"
            disabled={Boolean(errorMessage)}
            onClick={handleOnNext}
            loading={isSaving}
          >
            Save
          </UiButton>
        </UiDialog.Actions>
      </>
    );
  },
);
